import React, { FC, useEffect, useState, useRef, RefObject } from "react";
import {
  Paper,
  Typography,
  Avatar,
  Button,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import { Link, useLocation } from "react-router-dom";
import { X } from "lucide-react";
import Slider from "react-slick";
import CreatorSuggestionsModal from "./CreatorSuggestionsModal";

import {
  searchAll,
  getFileByIdAPI,
} from "../../../../api/userApi";
import useInViewPort from "../../../../hooks/interationObserver";
import { showToast } from "../../../../utils/toast";
import videoIconSvg from "../../../../assets/videoIcon.svg";
import defaultAvatars from "../../../../assets/defaultAvatars.svg";
import useCreatorSuggestionsStyles from "./suggestions-creators.styles";
import VerifiedShieldHelpIcon from "../../../../assets/VerifiedShieldHelpIcon";
import useMonetization from "../../../../hooks/useMonetization";
import {
  parseAdvancedFiltersFromSearch,
  serializeAdvancedFilters,
} from "./filtering";

enum FollowStatus {
  NOT_FOLLOWING = "not_following",
  FOLLOWING = "following",
  PENDING = "pending",
}

export interface CreatorSuggestion {
  _id: string;
  bio: string;
  profileImg: string;
  firstName: string;
  lastName: string;
  username: string;
  isSubs?: boolean;
  isSubscribed?: boolean;
  follow: {
    followUserId: string;
    accepted: boolean;
  } | null;
  userType?: {
    _id: string;
    title: "viewer" | "creator";
    description?: string;
  };
}

interface CreatorSuggestionsProps {
  onToggleVisibility?: () => void;
  registerOpenFilter?: (openFn: () => void) => void;
}

type SuggestionWithFollow = CreatorSuggestion & {
  followStatus: FollowStatus;
};

type ApiResponse = {
  items: CreatorSuggestion[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
};

const CreatorSuggestions: FC<CreatorSuggestionsProps> = ({
  onToggleVisibility,
  registerOpenFilter,
}) => {
  const VISIBLE_LIMIT = 7;
  const mobileSliderSettings = {
    dots: false,
    arrows: false,
    infinite: false,
    speed: 350,
    slidesToShow: 1,
    slidesToScroll: 1,
    centerMode: true,
    centerPadding: "36px",
    swipe: true,
    swipeToSlide: true,
    touchMove: true,
    draggable: true,
    touchThreshold: 8,
  };
  const classes = useCreatorSuggestionsStyles();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const lastCreatorRef = useRef<HTMLElement | undefined>(undefined);
  const inView = useInViewPort(lastCreatorRef, { threshold: 0.5 });
  const [suggestions, setSuggestions] = useState<SuggestionWithFollow[]>([]);
  const [mediaUrls, setMediaUrls] = useState<{ [key: string]: string }>({});
  const [, setPage] = useState<number>(1);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const { subscription } = useMonetization();
  const premiumTiers = ["onyx", "sapphire", "diamond", "platinum_vip"];
  const normalizedTier = subscription?.tier?.toLowerCase?.();
  const normalizedStatus = subscription?.status?.toLowerCase?.();
  const canUsePremiumFilters = Boolean(
    subscription &&
    normalizedStatus === "active" &&
    normalizedTier &&
    premiumTiers.includes(normalizedTier)
  );

  const fetchMediaUrl = async (mediaUrl: string) => {
    if (!mediaUrl) return videoIconSvg;
    // Якщо це Google URL (lh3.googleusercontent.com), використовуємо його напряму
    if (mediaUrl.includes("lh3.googleusercontent.com")) {
      setMediaUrls(prev => ({ ...prev, [mediaUrl]: mediaUrl }));
      return mediaUrl;
    }
    // Якщо це пряме посилання (https:// або http://), використовуємо його напряму
    if (mediaUrl.startsWith("https://") || mediaUrl.startsWith("http://")) {
      setMediaUrls(prev => ({ ...prev, [mediaUrl]: mediaUrl }));
      return mediaUrl;
    }
    const fileId = mediaUrl.split("/api/files/")[1];
    if (!fileId) return videoIconSvg;

    try {
      const blobUrl = await getFileByIdAPI(fileId);
      setMediaUrls(prev => ({ ...prev, [mediaUrl]: blobUrl }));
      return blobUrl;
    } catch (err) {
      console.error(`Error fetching avatar for ${mediaUrl}:`, err);
      showToast("Failed to load creator avatar", "error");
      return videoIconSvg;
    }
  };

  const getFollowStatus = (
    follow: CreatorSuggestion["follow"]
  ): FollowStatus => {
    if (!follow) {
      return FollowStatus.NOT_FOLLOWING;
    } else if (follow.accepted === true) {
      return FollowStatus.FOLLOWING;
    } else {
      return FollowStatus.PENDING;
    }
  };

  const shuffleArray = <T,>(array: T[]): T[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const fetchSuggestions = async (currentPage: number) => {
    if (isLoading) return;
    setLoading(true);

    try {
      const urlFilters = parseAdvancedFiltersFromSearch(location.search);
      const serializedFilters = serializeAdvancedFilters(urlFilters);
      const data = await searchAll("", {
        scope: "creators",
        page: currentPage,
        perPage: 8,
        excludeViewers: true,
        ...serializedFilters,
      });
      const { items, totalPages } = data as ApiResponse;

      const normalized = items
        .map((s: CreatorSuggestion) => ({
          _id: s._id,
          firstName: s.firstName,
          lastName: s.lastName,
          username: s.username,
          bio: s.bio,
          profileImg: s.profileImg,
          follow: s.follow,
          userType: s.userType,
          // API може віддавати різні прапорці: isSubs / isSubscribed / hasSubscribe
          // Нормалізуємо в один boolean, щоб UI завжди був консистентний.
          isSubs: Boolean(
            (s as any).isSubs || (s as any).hasSubscribe || (s as any).isSubscribed
          ),
          isSubscribed: Boolean(
            (s as any).isSubscribed || (s as any).hasSubscribe || (s as any).isSubs
          ),
          followStatus: getFollowStatus(s.follow),
        }))
        .filter(s => {
          if (!s.userType) {
            return true;
          }
          const isViewer = s.userType.title === "viewer";
          return !isViewer;
        }) as SuggestionWithFollow[];

      const shuffledSuggestions = shuffleArray(normalized);

      setSuggestions(prev => {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        const newSuggestions = [
          ...prev,
          ...shuffledSuggestions.filter(s => !prev.some(p => p._id === s._id)),
        ];
        return newSuggestions;
      });

      setHasMore(currentPage < totalPages);
    } catch (err) {
      console.error("Error fetching suggestions:", err);
      showToast(
        (err as Error)?.message || "Error loading suggestions!",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSuggestions([]);
    setPage(1);
    setHasMore(true);
    fetchSuggestions(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    if (inView && hasMore && !isLoading) {
      setPage(prev => {
        const nextPage = prev + 1;
        fetchSuggestions(nextPage);
        return nextPage;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView, hasMore, isLoading]);

  useEffect(() => {
    suggestions.forEach(creator => {
      if (creator.profileImg && !mediaUrls[creator.profileImg]) {
        fetchMediaUrl(creator.profileImg);
      }
    });
  }, [suggestions, mediaUrls]);

  const handleSeeMoreClick = React.useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const visibleSuggestions = suggestions.slice(0, VISIBLE_LIMIT);

  const renderCreatorCard = (
    creator: SuggestionWithFollow,
    idx: number
  ): React.ReactNode => {
    const showDivider = !isMobile && idx !== visibleSuggestions.length - 1;
    return (
      <Link
        key={creator._id}
        to={`/${creator.username}`}
        style={{ textDecoration: "none", color: "inherit" }}
      >
        <div
          className={classes.creatorItem}
          style={{
            borderBottom: showDivider ? "1px solid #FFFFFF1A" : "none",
            paddingBottom: showDivider ? 16 : 0,
            marginBottom: showDivider ? 16 : 0,
          }}
          ref={
            idx === suggestions.length - 1
              ? (lastCreatorRef as RefObject<HTMLDivElement>)
              : null
          }
        >
          <div className={classes.creatorInfo}>
            <div className={classes.avatarWrapper}>
              <Avatar
                src={mediaUrls[creator.profileImg] || defaultAvatars}
                className={classes.avatar}
              />
            </div>
            <div>
              <Typography variant="body1" className={classes.creatorName}>
                {creator.firstName} {creator.lastName}{" "}
                <VerifiedShieldHelpIcon className={classes.verifiedIcon} />
              </Typography>
              {!isMobile && creator?.username && (
                <Typography variant="body1" className={classes.creatorUserName}>
                  {`@${creator.username}`}
                </Typography>
              )}
              {isMobile && creator?.bio && (
                <Typography variant="body1" className={classes.creatorBio}>
                  {creator.bio}
                </Typography>
              )}
            </div>
          </div>
        </div>
      </Link>
    );
  };

  return (
    <>
      {isMobile && (
        <div className={classes.sidebarHeader}>
          <div
            className={classes.sidebarTitle}
            style={{
              width: "100%",
              marginBottom: isMobile ? 8 : 0,
            }}
          >
            <Typography variant="subtitle1" className={classes.creatorTitle}>
              Suggested creators:
            </Typography>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: isMobile ? "4px" : "8px",
              width: isMobile ? "100%" : "auto",
              justifyContent: isMobile ? "flex-end" : "flex-start",
            }}
          >
            {onToggleVisibility && (
              <Button
                size="small"
                onClick={onToggleVisibility}
                style={{
                  minWidth: "auto",
                  padding: isMobile ? "4px" : "4px",
                  color: "#FFFFFF",
                  fontSize: isMobile ? "12px" : "12px",
                }}
                title="Hide suggestions"
              >
                <X size={isMobile ? 18 : 20} color="#FFFFFF" />
              </Button>
            )}
          </div>
        </div>
      )}

      <Paper className={classes.sidebarCard}>
        {!isMobile && (
          <Typography variant="h6" component="h2" className={classes.sidebarTitleDesktop}>
            Suggested creators:
          </Typography>
        )}
        {isMobile ? (
          <div className={classes.mobileSliderWrapper}>
            <Slider {...mobileSliderSettings}>
              {visibleSuggestions.map((creator, idx) => (
                <div key={creator._id} className={classes.sliderItem}>
                  {renderCreatorCard(creator, idx)}
                </div>
              ))}
            </Slider>
          </div>
        ) : (
          visibleSuggestions.map((creator, idx) => (
            <React.Fragment key={creator._id}>
              {renderCreatorCard(creator, idx)}
            </React.Fragment>
          ))
        )}
        {!isMobile && (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-start",
              marginTop: suggestions.length ? 20 : 0,
            }}
          >
            <Typography
              variant="body2"
              className={classes.seeMore}
              onClick={handleSeeMoreClick}
            >
              Discover more creators
            </Typography>
          </div>
        )}
        {isLoading && (
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              padding: "10px",
            }}
          >
            <CircularProgress size={24} />
          </div>
        )}

        <CreatorSuggestionsModal
          open={isModalOpen}
          onClose={handleModalClose}
          canUsePremiumFilters={canUsePremiumFilters}
        />
      </Paper>
    </>
  );
};

export default CreatorSuggestions;
